import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'

// Reference resolution maps, bucketed by conversation. References are immutable with
// permanent citation ids, so upserts are idempotent and every citation resolves against
// its conversation's bucket. Buckets are never evicted on navigation — a background
// detail fetch for one conversation can never disturb another's resolution, and
// returning to a conversation finds its bucket still populated. Growth is bounded by
// conversations visited this session; nullifyData clears everything on logout.
export type AgentV2ReferencesState = {
  referencesByConversationId: Record<string, Record<string, AgentV2Reference>>
}

const initialState: AgentV2ReferencesState = {
  referencesByConversationId: {},
}

export const agentV2ReferencesSlice = createSlice({
  name: 'agentV2ReferencesState',
  initialState,
  reducers: {
    nullifyData: () => initialState,

    upsertReferences: (state, action: PayloadAction<{ conversationId: string; references: AgentV2Reference[] }>) => {
      const bucket = (state.referencesByConversationId[action.payload.conversationId] ??= {})
      // Both guards are silent defense-in-depth: payloads are validated and reported
      // upstream (agent-v2-reference-sanitizer at the ingest points), and reducers stay
      // side-effect free.
      for (const reference of action.payload.references) {
        if (reference.conversation_id !== action.payload.conversationId) continue
        // References are immutable with mint-once ids — the DB enforces
        // UNIQUE(conversation_id, citation_id) — so a duplicate id pointing at a
        // different row can only be a server or transport fault. Keep the first-seen
        // reference.
        const existing = bucket[reference.citation_id]
        if (existing && existing.reference_id !== reference.reference_id) continue
        bucket[reference.citation_id] = reference
      }
    },
  },
})

export const AgentV2ReferencesActions = agentV2ReferencesSlice.actions

export default agentV2ReferencesSlice.reducer
